import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * Move a server to another pool. If customers/resellers (or campaigns) still
 * reference it, require picking a replacement from the *current* pool first.
 */
export default function MoveServerPoolDialog({
  open,
  onOpenChange,
  server,
  pools = [],
  currentPoolId,
  samePoolServers = [],
  dependents = null,
  loadingDependents = false,
  busy = false,
  onConfirm,
  testIdPrefix = "move-pool",
  serverLabel = "server",
}) {
  const [targetId, setTargetId] = useState("");
  const [replacementId, setReplacementId] = useState("");

  useEffect(() => {
    if (!open) {
      setTargetId("");
      setReplacementId("");
    }
  }, [open, server?.id]);

  const others = useMemo(
    () => (pools || []).filter((p) => p.id && p.id !== (currentPoolId || server?.pool_id)),
    [pools, currentPoolId, server?.pool_id],
  );

  const customers = dependents?.customers || [];
  const resellers = dependents?.resellers || [];
  const campaigns = dependents?.campaigns || [];
  const needsReassign = customers.length + resellers.length + campaigns.length > 0;

  const altOptions = useMemo(
    () => (samePoolServers || []).filter((a) => a?.id && a.id !== server?.id),
    [samePoolServers, server?.id],
  );

  const canMove = !!targetId
    && others.some((p) => p.id === targetId)
    && !loadingDependents
    && (!needsReassign || (!!replacementId && altOptions.some((a) => a.id === replacementId)));

  const submit = () => {
    if (!canMove || busy) return;
    onConfirm?.(targetId, needsReassign ? replacementId : null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>Move server</DialogTitle>
          <DialogDescription>
            Move “{server?.name}” to another pool.
            {needsReassign
              ? " Assignments must first move to another server in this pool."
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {loadingDependents && (
            <p className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-deps-loading`}>
              Checking assignments…
            </p>
          )}

          {!loadingDependents && needsReassign && (
            <div
              className="space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3"
              data-testid={`${testIdPrefix}-reassign-required`}
            >
              <p className="text-sm font-medium">
                This {serverLabel} is assigned to accounts. Choose another {serverLabel}{" "}
                <span className="font-semibold">in this pool</span> before moving.
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 max-h-28 overflow-y-auto">
                {customers.slice(0, 8).map((c) => (
                  <li key={`c-${c.id}`}>Customer: {c.name || c.id}{c.company ? ` · ${c.company}` : ""}</li>
                ))}
                {resellers.slice(0, 8).map((r) => (
                  <li key={`r-${r.id}`}>Reseller: {r.name || r.id}{r.company ? ` · ${r.company}` : ""}</li>
                ))}
                {campaigns.slice(0, 8).map((c) => (
                  <li key={`camp-${c.id}`}>Campaign: {c.name || c.id}{c.status ? ` (${c.status})` : ""}</li>
                ))}
                {(customers.length + resellers.length + campaigns.length) > 24 && (
                  <li>…and more</li>
                )}
              </ul>
              <div className="space-y-2">
                <Label>Reassign to (same pool)</Label>
                {altOptions.length === 0 ? (
                  <p className="text-sm text-destructive" data-testid={`${testIdPrefix}-no-alternatives`}>
                    No other {serverLabel} in this pool. Add one first, then move.
                  </p>
                ) : (
                  <Select value={replacementId} onValueChange={setReplacementId}>
                    <SelectTrigger data-testid={`${testIdPrefix}-reassign-select`}>
                      <SelectValue placeholder={`Select replacement ${serverLabel}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {altOptions.map((a) => (
                        <SelectItem key={a.id} value={a.id} data-testid={`${testIdPrefix}-reassign-opt-${a.id}`}>
                          {a.name}{a.host || a.public_ip ? ` · ${a.host || a.public_ip}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Destination pool</Label>
            {others.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
                No other pools available. Create another pool first.
              </p>
            ) : (
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger data-testid={`${testIdPrefix}-select`}>
                  <SelectValue placeholder="Select pool…" />
                </SelectTrigger>
                <SelectContent>
                  {others.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`${testIdPrefix}-option-${p.id}`}>
                      {p.name}{p.is_default ? " (default)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            className="rounded-lg"
            disabled={!canMove || busy || (needsReassign && altOptions.length === 0)}
            onClick={submit}
            data-testid={`${testIdPrefix}-confirm`}
          >
            {busy ? "Moving…" : needsReassign ? "Reassign & move" : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
